import type { Labelled } from './common.js';

/**
 * ثوابت وحدة الموارد البشرية.
 */

/* ==========================================================================
   الموظفون
   ========================================================================== */

export const EMPLOYEE_STATUSES = [
  'PENDING',
  'ACTIVE',
  'ON_LEAVE',
  'SUSPENDED',
  'TERMINATED',
] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export interface EmployeeStatusDef extends Labelled<EmployeeStatus> {
  /** خارج رأس العمل: لا يدخل في المرتبات ولا يُنتظر حضوره. */
  inactive: boolean;
}

export const EMPLOYEE_STATUS_DEFS: readonly EmployeeStatusDef[] = [
  /*
   * مرشَّحٌ للتعيين: فُتح ملفُّه ولم يبدأ.
   *
   * خارجَ رأس العمل بكلّ معنًى — لا مرتّبَ ولا حضورَ ولا عدٌّ في القوى
   * العاملة. وقبولُ العرض وعدٌ، والعقدُ الموقَّعُ هو الالتزام: بينهما
   * يُراجَع الطبُّ وتصل الشهاداتُ وقد يعتذر صاحبُه لعرضٍ آخر.
   */
  { key: 'PENDING', label: 'مرشَّح للتعيين', kind: 'warning', inactive: true },
  { key: 'ACTIVE', label: 'على رأس العمل', kind: 'success', inactive: false },
  { key: 'ON_LEAVE', label: 'في إجازة', kind: 'info', inactive: false },
  { key: 'SUSPENDED', label: 'موقوف', kind: 'warning', inactive: true },
  { key: 'TERMINATED', label: 'انتهت خدمته', kind: 'neutral', inactive: true },
] as const;

export const EMPLOYEE_STATUS_MAP = Object.fromEntries(
  EMPLOYEE_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<EmployeeStatus, EmployeeStatusDef>>;

/** من يدخل في دورة المرتب. الموقوف والمنتهية خدمته لا قسيمة لهما. */
export const PAYABLE_STATUSES = EMPLOYEE_STATUS_DEFS.filter((d) => !d.inactive).map(
  (d) => d.key,
) as readonly EmployeeStatus[];

/* ==========================================================================
   عقود العمل
   ========================================================================== */

export const JOB_CONTRACT_STATUSES = [
  'DRAFT',
  'PENDING',
  'SIGNED',
  'EXPIRED',
  'TERMINATED',
] as const;
export type JobContractStatus = (typeof JOB_CONTRACT_STATUSES)[number];

export interface JobContractStatusDef extends Labelled<JobContractStatus> {
  description: string;
  /** هل صاحبُه على رأس العمل بهذا العقد. */
  live: boolean;
}

export const JOB_CONTRACT_STATUS_DEFS: readonly JobContractStatusDef[] = [
  {
    key: 'DRAFT',
    label: 'مسوّدة',
    kind: 'neutral',
    description: 'يُكتب ويُراجَع — لا يُعتدّ به.',
    live: false,
  },
  {
    key: 'PENDING',
    label: 'بانتظار التوقيع',
    kind: 'warning',
    description: 'خرج للتوقيع ويُنتظر.',
    live: false,
  },
  {
    key: 'SIGNED',
    label: 'تم التوقيع',
    kind: 'success',
    description: 'وُقّع — وبه صار صاحبُه على رأس العمل.',
    live: true,
  },
  {
    key: 'EXPIRED',
    label: 'منتهٍ',
    kind: 'neutral',
    description: 'انقضت مدّتُه ولم يُجدَّد.',
    live: false,
  },
  {
    key: 'TERMINATED',
    label: 'أُنهي',
    kind: 'danger',
    description: 'أُنهي قبل مدّته.',
    live: false,
  },
] as const;

export const JOB_CONTRACT_STATUS_MAP = Object.fromEntries(
  JOB_CONTRACT_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<JobContractStatus, JobContractStatusDef>>;

/**
 * ما يُقرأ من عقدٍ عن قربِ انتهائه.
 *
 * العقدُ المؤقّتُ ينتهي في يومٍ بعينه، ومن لا يُنبَّه قبله بشهرٍ يجد موظّفًا
 * يعمل بلا عقدٍ أو يخرج فجأةً. والثلاثون يومًا عرفٌ يكفي لتجديدٍ أو إشعار.
 */
export const JOB_CONTRACT_WARN_DAYS = 30;

/** أيّامٌ حتى انتهاء العقد — سالبٌ يعني أنّه انقضى. */
export function jobContractDaysLeft(
  endsOn: string | Date | null,
  today = new Date(),
): number | null {
  if (!endsOn) return null;
  const end = new Date(
    typeof endsOn === 'string' ? `${endsOn}T00:00:00.000Z` : endsOn,
  );
  const now = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  return Math.round((end.getTime() - now) / 86_400_000);
}

/* ==========================================================================
   أقسام العمل — القسمُ يقرّر أين يظهر الموظّف
   ========================================================================== */

/**
 * قسمُ الموظّف، ومعه القسمُ الذي يظهر فيه في النظام.
 *
 * كان «القسم» حقلًا نصّيًّا حرًّا، فلم يكن يعني شيئًا للنظام: كلُّ موظّفٍ
 * يظهر في كلّ فريقٍ لأنّ لا سبيل إلى معرفة أين موضعه. ومن كتب «مبيعات»
 * ومن كتب «المبيعات» ومن كتب «قسم المبيعات» ثلاثةُ أقسامٍ في نظر الحاسوب.
 *
 * فصار القسمُ اختيارًا من كتالوج، ولكلّ قسمٍ **موضعُه**: من عُيّن في
 * المبيعات يظهر في فريق المبيعات، ومن عُيّن في العمليات يظهر في فريق
 * إدارة المشاريع. القرارُ يُتَّخذ مرّةً في ملفّ الموظّف ويسري في النظام
 * كلِّه — لا وسمٌ يُضاف في كلّ شاشةٍ على حدة فيُنسى في إحداها.
 */
export const WORK_SECTIONS = ['sales', 'agency', 'callcenter', 'back_office'] as const;
export type WorkSection = (typeof WORK_SECTIONS)[number];

export interface DepartmentDef extends Labelled<string> {
  /** أين يظهر أهلُ هذا القسم في النظام. */
  section: WorkSection;
  hint: string;
}

export const DEPARTMENT_DEFS: readonly DepartmentDef[] = [
  {
    key: 'SALES',
    label: 'المبيعات',
    kind: 'success',
    section: 'sales',
    hint: 'يظهر في فريق المبيعات، وله هدفٌ ونسبةُ عمولة',
  },
  {
    key: 'OPERATIONS',
    label: 'العمليات وإدارة المشاريع',
    kind: 'brand',
    section: 'agency',
    hint: 'يظهر في فريق العمل بإدارة المشاريع، وتُسنَد إليه مهامّ',
  },
  {
    key: 'CREATIVE',
    label: 'التصميم والإبداع',
    kind: 'info',
    section: 'agency',
    hint: 'ينفّذ مهامَّ المشاريع — يظهر في فريق إدارة المشاريع',
  },
  {
    key: 'PRODUCTION',
    label: 'الإنتاج والتصوير',
    kind: 'info',
    section: 'agency',
    hint: 'ينفّذ مهامَّ المشاريع — يظهر في فريق إدارة المشاريع',
  },
  {
    key: 'TECH',
    label: 'التطوير والتقنية',
    kind: 'info',
    section: 'agency',
    hint: 'ينفّذ مهامَّ المشاريع — يظهر في فريق إدارة المشاريع',
  },
  {
    key: 'ACCOUNT_MGMT',
    label: 'إدارة الحسابات وخدمة العملاء',
    kind: 'brand',
    section: 'agency',
    hint: 'يتابع عملاء المشاريع — يظهر في فريق إدارة المشاريع',
  },
  {
    key: 'CALLCENTER',
    label: 'مركز الاتصال',
    kind: 'warning',
    section: 'callcenter',
    hint: 'يعمل على المكالمات — يظهر في مركز الاتصال',
  },
  {
    key: 'FINANCE',
    label: 'الحسابات والمالية',
    kind: 'neutral',
    section: 'back_office',
    hint: 'إسنادٌ إداريّ — لا يظهر في فرق التنفيذ',
  },
  {
    key: 'HR',
    label: 'الموارد البشرية',
    kind: 'neutral',
    section: 'back_office',
    hint: 'إسنادٌ إداريّ — لا يظهر في فرق التنفيذ',
  },
  {
    key: 'ADMIN',
    label: 'الإدارة العليا',
    kind: 'neutral',
    section: 'back_office',
    hint: 'إسنادٌ إداريّ — لا يظهر في فرق التنفيذ',
  },
] as const;

export const DEPARTMENT_MAP = Object.fromEntries(
  DEPARTMENT_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<string, DepartmentDef>>;

export const DEPARTMENT_KEYS = DEPARTMENT_DEFS.map((d) => d.key) as readonly string[];

/** أقسامُ قسمٍ بعينه — «من يظهر في إدارة المشاريع؟». */
export function departmentsOf(section: WorkSection): readonly string[] {
  return DEPARTMENT_DEFS.filter((d) => d.section === section).map((d) => d.key);
}

/**
 * قراءةُ قسمِ موظّفٍ قديمٍ كُتب نصًّا حرًّا.
 *
 * البياناتُ القائمة فيها «مبيعات» و«المبيعات» و«تسويق» — تُطابَق بالتسمية
 * بعد تجريدها من «ال» والمسافات، فمن كُتب قسمُه صحيحًا لا يحتاج إدخالًا
 * ثانيًا. وما لا يُطابق يبقى بلا مفتاح ويُقال لصاحبه ذلك صراحةً.
 */
export function guessDepartmentKey(free: string | null | undefined): string | null {
  if (!free) return null;
  const norm = (s: string) => s.replace(/^ال/, '').replace(/\s+/g, '').trim();
  const needle = norm(free);
  if (!needle) return null;

  const direct = DEPARTMENT_DEFS.find((d) => norm(d.label) === needle);
  if (direct) return direct.key;

  const ALIASES: Record<string, string> = {
    مبيعات: 'SALES',
    بيع: 'SALES',
    تسويق: 'SALES',
    عمليات: 'OPERATIONS',
    أوبريشن: 'OPERATIONS',
    اوبريشن: 'OPERATIONS',
    مشاريع: 'OPERATIONS',
    مشروعات: 'OPERATIONS',
    تصميم: 'CREATIVE',
    جرافيك: 'CREATIVE',
    إبداع: 'CREATIVE',
    ابداع: 'CREATIVE',
    إنتاج: 'PRODUCTION',
    انتاج: 'PRODUCTION',
    تصوير: 'PRODUCTION',
    مونتاج: 'PRODUCTION',
    تطوير: 'TECH',
    برمجة: 'TECH',
    تقنية: 'TECH',
    'خدمةالعملاء': 'ACCOUNT_MGMT',
    'إدارةالحسابات': 'ACCOUNT_MGMT',
    'مركزالاتصال': 'CALLCENTER',
    كولسنتر: 'CALLCENTER',
    حسابات: 'FINANCE',
    مالية: 'FINANCE',
    'مواردبشرية': 'HR',
    إدارة: 'ADMIN',
    ادارة: 'ADMIN',
  };
  return ALIASES[needle] ?? null;
}

/* ==========================================================================
   الحضور
   ========================================================================== */

export const ATTENDANCE_STATUSES = [
  'PRESENT',
  'LATE',
  'ABSENT',
  'LEAVE',
  'WEEKEND',
  'HOLIDAY',
  'PENDING',
] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

export interface AttendanceStatusDef extends Labelled<AttendanceStatus> {
  /** يُحتسب يوم عمل مدفوعًا في المرتب. */
  paid: boolean;
  /** يوم دوام مطلوب أصلًا — يميّز الغياب عن العطلة. */
  workday: boolean;
}

export const ATTENDANCE_STATUS_DEFS: readonly AttendanceStatusDef[] = [
  { key: 'PRESENT', label: 'حاضر', kind: 'success', paid: true, workday: true },
  { key: 'LATE', label: 'متأخر', kind: 'warning', paid: true, workday: true },
  { key: 'ABSENT', label: 'غائب', kind: 'danger', paid: false, workday: true },
  { key: 'LEAVE', label: 'إجازة', kind: 'info', paid: true, workday: true },
  { key: 'WEEKEND', label: 'عطلة أسبوعية', kind: 'neutral', paid: true, workday: false },
  { key: 'HOLIDAY', label: 'عطلة رسمية', kind: 'neutral', paid: true, workday: false },
  { key: 'PENDING', label: 'لم يُسجَّل', kind: 'neutral', paid: false, workday: true },
] as const;

export const ATTENDANCE_STATUS_MAP = Object.fromEntries(
  ATTENDANCE_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<AttendanceStatus, AttendanceStatusDef>>;

/**
 * أيام الأسبوع بترتيب getDay في جافاسكريبت.
 *
 * الأحد صفر لا الاثنين، لأن هذا ما يعطيه التاريخ نفسه. الترتيب هنا مقصود
 * ليُفهرَس مباشرة بـ getDay() بلا حساب وسيط يخطئ أحدهم فيه.
 */
export const WEEKDAYS = [
  { index: 0, label: 'الأحد', short: 'أحد' },
  { index: 1, label: 'الاثنين', short: 'اثنين' },
  { index: 2, label: 'الثلاثاء', short: 'ثلاثاء' },
  { index: 3, label: 'الأربعاء', short: 'أربعاء' },
  { index: 4, label: 'الخميس', short: 'خميس' },
  { index: 5, label: 'الجمعة', short: 'جمعة' },
  { index: 6, label: 'السبت', short: 'سبت' },
] as const;

/** العطلة الأسبوعية الافتراضية في مصر والخليج: الجمعة والسبت. */
export const DEFAULT_WEEKEND: readonly number[] = [5, 6];

/* ==========================================================================
   الإجازات
   ========================================================================== */

export const LEAVE_TYPES = ['ANNUAL', 'SICK', 'EMERGENCY', 'UNPAID'] as const;
export type LeaveType = (typeof LEAVE_TYPES)[number];

export interface LeaveTypeDef extends Labelled<LeaveType> {
  /** يُخصم من الرصيد السنوي. */
  deductsBalance: boolean;
  /** مدفوعة — لا تُخصم من المرتب. */
  paid: boolean;
}

export const LEAVE_TYPE_DEFS: readonly LeaveTypeDef[] = [
  { key: 'ANNUAL', label: 'سنوية', kind: 'info', deductsBalance: true, paid: true },
  { key: 'SICK', label: 'مرضية', kind: 'warning', deductsBalance: false, paid: true },
  { key: 'EMERGENCY', label: 'عارضة', kind: 'warning', deductsBalance: true, paid: true },
  { key: 'UNPAID', label: 'بدون أجر', kind: 'neutral', deductsBalance: false, paid: false },
] as const;

export const LEAVE_TYPE_MAP = Object.fromEntries(
  LEAVE_TYPE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<LeaveType, LeaveTypeDef>>;

/**
 * أنواعُ الإجازات التي لا أجرَ لها — يقرؤها حسابُ المرتّب.
 *
 * كانت رايةُ `paid` معرَّفةً في الكتالوج ولا يقرؤها أحد: ورقةُ الحضور تكتب
 * «إجازة» ولا تكتب نوعَها، وحالةُ «إجازة» في كتالوج الحضور مدفوعة — فكانت
 * الإجازةُ بدون أجرٍ تُدفع كاملةً. تُشتقُّ من الكتالوج ولا تُكتب قائمةً
 * ثانية: قائمتان تعنيان نوعًا يُضاف في إحداهما وتنساه الأخرى.
 */
export const UNPAID_LEAVE_TYPES = LEAVE_TYPE_DEFS.filter((d) => !d.paid).map(
  (d) => d.key,
) as readonly LeaveType[];

export const LEAVE_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];

export const LEAVE_STATUS_DEFS: readonly Labelled<LeaveStatus>[] = [
  { key: 'PENDING', label: 'قيد الاعتماد', kind: 'warning' },
  { key: 'APPROVED', label: 'معتمدة', kind: 'success' },
  { key: 'REJECTED', label: 'مرفوضة', kind: 'danger' },
  { key: 'CANCELLED', label: 'ملغاة', kind: 'neutral' },
] as const;

export const LEAVE_STATUS_MAP = Object.fromEntries(
  LEAVE_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<LeaveStatus, Labelled<LeaveStatus>>>;

/* ==========================================================================
   بنود المرتب
   ========================================================================== */

export const ADJUSTMENT_KINDS = [
  'BONUS',
  'INCENTIVE',
  'OVERTIME',
  'REIMBURSEMENT',
  'DEDUCTION',
  'PENALTY',
] as const;
export type AdjustmentKind = (typeof ADJUSTMENT_KINDS)[number];

export interface AdjustmentKindDef extends Labelled<AdjustmentKind> {
  /**
   * الإشارة: ‎+1 يُضاف و‎-1 يُخصم.
   *
   * المبلغ يُخزَّن موجبًا دائمًا والإشارة تأتي من النوع. تخزين السالب يجعل
   * "خصم بمبلغ سالب" يعني زيادة، وهو خطأ لا تكشفه أي شاشة.
   */
  sign: 1 | -1;
}

export const ADJUSTMENT_KIND_DEFS: readonly AdjustmentKindDef[] = [
  { key: 'BONUS', label: 'مكافأة', kind: 'success', sign: 1 },
  { key: 'INCENTIVE', label: 'حافز', kind: 'success', sign: 1 },
  { key: 'OVERTIME', label: 'عمل إضافي', kind: 'success', sign: 1 },
  /* مالُ الموظّف يعود إليه — لا منحةٌ تُحسب في متوسّط أجره. */
  { key: 'REIMBURSEMENT', label: 'ردّ مصروفات', kind: 'info', sign: 1 },
  { key: 'DEDUCTION', label: 'خصم', kind: 'danger', sign: -1 },
  { key: 'PENALTY', label: 'جزاء', kind: 'danger', sign: -1 },
] as const;

export const ADJUSTMENT_KIND_MAP = Object.fromEntries(
  ADJUSTMENT_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<AdjustmentKind, AdjustmentKindDef>>;

/* ==========================================================================
   دورات المرتب
   ========================================================================== */

export const PAYROLL_RUN_STATUSES = ['DRAFT', 'APPROVED', 'PAID'] as const;
export type PayrollRunStatus = (typeof PAYROLL_RUN_STATUSES)[number];

export interface PayrollRunStatusDef extends Labelled<PayrollRunStatus> {
  /** لا تُعاد المحاسبة بعدها ولا تُحذف. */
  locked: boolean;
}

export const PAYROLL_RUN_STATUS_DEFS: readonly PayrollRunStatusDef[] = [
  { key: 'DRAFT', label: 'مسودة', kind: 'neutral', locked: false },
  { key: 'APPROVED', label: 'معتمدة', kind: 'info', locked: true },
  { key: 'PAID', label: 'مدفوعة', kind: 'success', locked: true },
] as const;

export const PAYROLL_RUN_STATUS_MAP = Object.fromEntries(
  PAYROLL_RUN_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<PayrollRunStatus, PayrollRunStatusDef>>;

/* ==========================================================================
   حساب المرتب
   ========================================================================== */

/**
 * أيام الشهر المعتمدة في قسمة المرتب اليومي.
 *
 * ثلاثون ثابتة لا عدد أيام الشهر الفعلي: هذا هو العرف في مصر والخليج،
 * وبه لا يختلف أجر اليوم بين فبراير ومارس فيسأل الموظف عن الفرق.
 */
export const PAYROLL_DAYS_PER_MONTH = 30;

export function dailyRate(baseSalary: number): number {
  return baseSalary / PAYROLL_DAYS_PER_MONTH;
}

/** أجر الساعة على أساس ثماني ساعات في اليوم. */
export const WORK_HOURS_PER_DAY = 8;

export function hourlyRate(baseSalary: number): number {
  return dailyRate(baseSalary) / WORK_HOURS_PER_DAY;
}

export interface PayslipLine {
  kind: string;
  label: string;
  amount: number;
  note?: string;
}

/** أول يوم في شهر تاريخ ما، بلا وقت — مفتاح دورة المرتب. */
export function periodStart(value: Date | string): Date {
  const d = new Date(value);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

const MONTHS = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
] as const;

export function periodLabel(value: Date | string): string {
  const d = new Date(value);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
